import React, { useState } from 'react';
import { useIntl } from "react-intl";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { EntityTypeLabel, LabelKeyValueItem } from '@/model';
import entityRepository from "@/repository/entityRepository";
import { usePaginatedEntities } from "@/query/entity";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { useAgentId, useModal, usePageParam } from "@/hook";
import Button from "@/component/Button";
import PaginationBar from "@/component/PaginationBar";
import { UploadModal } from "@/component/modal";
import { TypeSearchbar } from "@/component/input";
import { SingleTestPanel, SingleTestButton } from '@/component/singleTest';
import { AddIcon, DownloadIcon, UploadIcon } from "@/assets/svgs/icons";

import EntityTable from "./component/EntityTable";
import EntityModifyModal from "./component/EntityModifyModal";
import EntityAddModal, { ENTITY_CREATE_MODAL_ID } from "./component/EntityAddModal";


const ENTITY_PAGE_SIZE = 15;
const ENTITY_UPLOAD_MODAL_ID = "entity-upload-modal";

const ENTITY_FILTER_OPTIONS: EntityTypeLabel[] = ['normal', 'post', 'ignore'];

const SEARCH_TYPE_OPTIONS: LabelKeyValueItem[] = [
    { labelKey: "i05050", value: "name" },
    { labelKey: "i05049", value: "entity_value" }
];

export type SortBy = {
    type: 'name' | 'type',
    order: 'asc' | 'desc'
}

export interface EntityPageParam {
    page: number;
    size: number;
    searchKeyword: string;
    searchType: "name" | "entity_value";
    sortBy: SortBy;
    typeParams: string;
}

const EntityView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();

    const { param, set: setParam } = usePageParam<EntityPageParam>({
        page: 0,
        size: ENTITY_PAGE_SIZE,
        searchKeyword: "",
        searchType: "name",
        sortBy: {
            type: "name",
            order: "asc"
        },
        typeParams: 'normal-entity,post-entity,ignore-entity',
    });
    const [checkboxStates, setCheckboxStates] = useState<Record<EntityTypeLabel, boolean>>({ normal: true, post: true, ignore: true });

    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: entitiesPage } = usePaginatedEntities({
        agentId,
        page: param?.page,
        size: param?.size,
        is_option_sort: param?.sortBy.type === 'type',
        option_list: param?.typeParams,
        sort: param?.sortBy.order,
        keyword: param?.searchKeyword,
        searchType: param?.searchType,
    });

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        return await entityRepository.import(agentId, file, isCleanImport);
    };

    const onCheckboxChange = (type: EntityTypeLabel) => {
        const newStates = { ...checkboxStates, [type]: !checkboxStates[type] };
        const newStateParam =
            Object.entries(newStates)
                .filter(([_, isChecked]) => isChecked)
                .map(([key]) => `${key}-entity`)
                .join(",");

        setCheckboxStates(newStates);
        setParam({ typeParams: newStateParam });
    };

    const onChangeSort = (type: 'name' | 'type') => {
        if (type === param.sortBy.type) {
            setParam({
                ...param,
                sortBy: {
                    ...param.sortBy,
                    order: param.sortBy.order === "asc" ? "desc" : "asc",
                },
            });
        } else {
            setParam({
                ...param,
                sortBy: {
                    ...param.sortBy,
                    type: type,
                },
            });
        }
    };

    return (<>
        <EntityAddModal/>
        <EntityModifyModal />
        <UploadModal id={ENTITY_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i05021" })}
                     cleanImportText={intl.formatMessage({ id: "i05048" })}
                     sampleFileName="nlu_entity_sample"
                     sampleFileColumns={[
                         { attribute: "entity_name", description: intl.formatMessage({ id: "i05015" }) },
                         { attribute: "keyword", description: intl.formatMessage({ id: "i05016" }) },
                         { attribute: "synonym", description: intl.formatMessage({ id: "i05017" }) },
                         { attribute: "mapping_value", description: intl.formatMessage({ id: "i05018" }) },
                         { attribute: "ignored", description: intl.formatMessage({ id: "i05019" }) },
                         { attribute: "post_processing", description: intl.formatMessage({ id: "i05020" }) },
                     ]}
                     upload={onClickUpload}
        />

        <div className={classNames(
                 "agent-content-layout flex flex-col",
                 singleTestPanelOpen && "test-panel-open",
             )}
        >
            <div className="search-bar-container mb-4.5">
                <div className="flex items-center gap-2">
                    <TypeSearchbar width={560}
                                   searchTypes={SEARCH_TYPE_OPTIONS}
                                   onSearch={(query, option) => setParam({ searchKeyword: query, searchType: option.value })}
                    />
                    <div className="flex justify-start items-center gap-3 text-[14px] text-black-500 font-[500]">
                        {ENTITY_FILTER_OPTIONS.map((type) => (
                            <label key={type}
                                   className="h-5.5 flex items-center gap-1 cursor-pointer"
                            >
                                <input type="checkbox"
                                       className="selection-table-checkbox !size-3.5"
                                       checked={checkboxStates[type]}
                                       onChange={() => onCheckboxChange(type)}
                                />
                                {type.toUpperCase()}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="table-btn-container w-md-page mb-2.5">
                <Button variant="text"
                        onClick={() => modal.open(ENTITY_CREATE_MODAL_ID)}
                >
                    <AddIcon className="size-3.5"/>
                    {intl.formatMessage({ id: "i05005" })}
                </Button>

                <div className="flex self-end gap-1">
                    <Button variant="text"
                            onClick={() => modal.open(ENTITY_UPLOAD_MODAL_ID)}
                    >
                        <UploadIcon className="size-3.5" />
                        {intl.formatMessage({ id: "b0004" })}
                    </Button>

                    <Button variant="text"
                            onClick={() => entityRepository.export(agentId)}
                    >
                        <DownloadIcon className="size-3.5" />
                        {intl.formatMessage({ id: "b0005" })}
                    </Button>

                    <SingleTestButton />
                </div>
            </div>

            <div className="table-test-container">
                <div className="table-pagination-container w-full max-w-md-page">
                    <EntityTable page={entitiesPage}
                                 pageParam={param}
                                 onClickSortHead={onChangeSort}
                    />

                    <PaginationBar pageData={entitiesPage}
                                   onClickPage={page => setParam({ page: page })}
                    />
                </div>

                <SingleTestPanel />
            </div>
        </div>
    </>);
};

export default EntityView;
